<script setup lang="ts">
import { computed, defineAsyncComponent, getCurrentInstance, watch } from 'vue'
import { useRoute } from 'vue-router'
import AppBar from '@/components/AppBar.vue'
import AppNavDrawer from '@/components/AppNavDrawer.vue'
import AppPageTitle from '@/components/AppPageTitle.vue'
import UpdatePrompt from '@/components/UpdatePrompt.vue'
import ApiUnreachableBanner from '@/components/ApiUnreachableBanner.vue'
import AccessDeniedSnackbar from '@/components/AccessDeniedSnackbar.vue'
import FbSnackbarHost from '@/components/ui/FbSnackbarHost.vue'
import BottomNavigation from '@/components/BottomNavigation.vue'
import HelpDialog from '@/components/HelpDialog.vue'
import { usePWA } from '@/composables/usePWA'
import { useSafeArea } from '@/composables/useSafeArea'
import { usePwaDisplayClass } from '@/composables/usePwaDisplayClass'
import { useShellLayout } from '@/composables/useShellLayout'
import { useShellCounts } from '@/composables/useShellCounts'
import { useLogger } from './composables/useLogger'
import { captureError } from '@/utils/sentry'
import { useAuthStore } from '@/stores/auth'
import { useChoresStore } from '@/stores/chores'
import { useCompletionQueueStore } from '@/stores/completionQueue'
import PendingSyncBanner from '@/components/PendingSyncBanner.vue'
import { useOnboardingStore } from '@/stores/onboarding'
import InstallPromptDialog from '@/components/InstallPromptDialog.vue'
import { useInstallPromptStore } from '@/stores/installPrompt'
import { detectPlatform } from '@/utils/platform'
import { useSSE } from '@/composables/useSSE'

const DevPanel = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@/components/DevPanel.vue'))
  : { render: () => null }

const { isInstalled, isInstallable, installPWA } = usePWA()
const { logger } = useLogger()
useSafeArea()
usePwaDisplayClass(isInstalled)
const { layout: shellLayout } = useShellLayout()
const layout = computed(() => shellLayout.value)
const route = useRoute()
useShellCounts()

const authStore = useAuthStore()
const onboarding = useOnboardingStore()
const installPrompt = useInstallPromptStore()
const platform = detectPlatform()
const choresStore = useChoresStore()
const completionQueue = useCompletionQueueStore()
const { connect, disconnect } = useSSE()

watch(
  () => authStore.accountId,
  (accountId) => {
    completionQueue.stopAutoReplay()
    completionQueue.$reset()
    if (accountId) {
      connect()
      completionQueue.startAutoReplay()
    } else {
      disconnect()
    }
  },
  { immediate: true }
)

// Refresh chores after queued completions sync.
watch(
  () => completionQueue.lastSyncedAt,
  async (syncedAt) => {
    if (syncedAt === null) return
    try {
      await choresStore.fetchChores()
    } catch (err) {
      logger.error('Failed to refresh chores after sync', {}, err instanceof Error ? err : new Error(String(err)))
    }
  }
)

watch(
  () => [authStore.isAuthenticated, authStore.isAdult, authStore.user?.has_seen_onboarding] as const,
  ([authed, adult, seen]) => {
    if (authed && adult && seen === false) {
      onboarding.autoTrigger()
    } else if (authed && seen !== false && !isInstalled.value) {
      // Adults after onboarding; children (seen === undefined) right after QR login.
      installPrompt.autoTrigger(isInstalled.value)
    }
  },
  { immediate: true }
)

// Close the prompt if the PWA is installed via any path while it is open.
watch(isInstalled, (installed) => {
  if (installed) installPrompt.installed()
})

// Set up global error handler
const app = getCurrentInstance()?.appContext.app
if (app) {
  app.config.errorHandler = (err, instance, info) => {
    const componentName = (instance as {$options?: {name?: string}}).$options?.name || 'UnknownComponent'
    const error = err instanceof Error ? err : new Error(String(err))
    logger.error('Uncaught error', { component: componentName, info }, error)
    captureError(error, { component: componentName, info, source: 'vue.errorHandler' })
  }
}

</script>

<template>
  <v-app>
    <AppBar v-if="(layout === 'guest' || layout === 'bottom') && !route.meta.hideAppBar" />
    <AppNavDrawer v-if="layout === 'drawer' || layout === 'rail'" :rail="layout === 'rail'" />
    <v-main data-testid="main-content" :class="{ 'fb-main--side-nav': layout === 'drawer' || layout === 'rail' }">
      <AppPageTitle v-if="layout === 'drawer' || layout === 'rail'" />
      <router-view />
    </v-main>
    <div class="fb-banner-stack">
      <UpdatePrompt />
      <ApiUnreachableBanner />
      <PendingSyncBanner />
    </div>
    <AccessDeniedSnackbar />
    <FbSnackbarHost />
    <HelpDialog
      v-model="onboarding.isOpen"
      :persistent="onboarding.persistent"
      @finish="onboarding.finish"
      @dismiss="onboarding.close"
    />
    <InstallPromptDialog
      v-model="installPrompt.isOpen"
      :platform="platform"
      :can-install="isInstallable"
      :install="installPWA"
      @installed="installPrompt.installed"
      @remind-later="installPrompt.remindLater"
      @dont-remind="installPrompt.dontRemindAgain"
    />
    <DevPanel />
    <BottomNavigation v-if="layout === 'bottom'" />
  </v-app>
</template>

<style>
/*
 * Safe Area Support for Devices with Notches and Home Indicators
 *
 * IMPORTANT: Requires viewport-fit=cover in index.html:
 * <meta name="viewport" content="..., viewport-fit=cover">
 *
 * Without viewport-fit=cover, env(safe-area-inset-*) always returns 0.
 */

:root {
  /* Pre-JS fallbacks: useSafeArea() overwrites these with resolved pixel
   * values on mount to handle iOS cold-start env() timing. */
  --safe-area-inset-top: env(safe-area-inset-top, 0px);
  --safe-area-inset-right: env(safe-area-inset-right, 0px);
  --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
  --safe-area-inset-left: env(safe-area-inset-left, 0px);
  /* Correct the iOS standalone layout-viewport shortfall after measurement. */
  --viewport-bottom-gap: 0px;
}

/* Prevent white bar at bottom when content extends into safe area */
html {
  min-height: 100%;
  /* Allow pan/pinch-zoom but disable double-tap-to-zoom */
  touch-action: manipulation;
}

/* 
 * Safe Area Strategy:
 * - Apply safe area padding only to edge elements (app-bar, bottom-nav)
 * - Do NOT apply to .v-application root to prevent modal coordinate issues
 * - Modals (v-dialog) calculate positions based on viewport without safe area offset
 */
/* Apply left/right safe area padding to constrain content within side notches */
.v-application {
  padding-left: var(--safe-area-inset-left);
  padding-right: var(--safe-area-inset-right);
  background-color: rgb(var(--v-theme-background)) !important;
}

/* Ensure app bar respects safe area at top and stays above content */
.v-app-bar {
  padding-top: var(--safe-area-inset-top) !important;
  z-index: 1005 !important;
}

/* Top clearance for main content and dialogs must apply in ALL display
 * modes, not just standalone/installed — .v-app-bar's safe-area padding
 * above is itself ungated and renders in regular browser tabs too on
 * notched devices (viewport-fit=cover is set unconditionally in
 * index.html). Without this, content/dialogs sit partially underneath
 * the app bar in non-standalone mode. */
.v-main {
  padding-top: calc(var(--v-layout-top, 64px) + var(--safe-area-inset-top)) !important;
}

/* Vuetify does not reserve the drawer's 16px margin. */
.fb-main--side-nav {
  padding-left: calc(var(--v-layout-left, 0px) + 16px) !important;
}

/* .v-dialog centers its content via flex align-items on the overlay box
 * (Vuetify's VDialog.css), so a margin-top on the content only moves it
 * ~half as far as intended (the browser's centering math treats the
 * margin as consumed space). Constrain the overlay box itself instead,
 * so centering happens within the space already below the app bar.
 * Scoped to centered dialogs: sheets anchor to the bottom and clear the notch
 * via their own max-height, so they must not inherit this top offset. */
.v-dialog:not(.fb-bottom-sheet) {
  top: calc(var(--v-layout-top, 64px) + var(--safe-area-inset-top)) !important;
}

.v-dialog:not(.fb-bottom-sheet) .v-overlay__content {
  max-height: calc(100dvh - var(--v-layout-top, 64px) - var(--safe-area-inset-top)) !important;
}

/* iOS standalone touch-offset workaround (#430; see prior #313).
 *
 * On iOS, while a fixed overlay is open, any document scroll offset makes
 * WebKit mis-map touch coordinates so taps land below their target. On-device
 * measurement showed the document still scrolling *during* dialog interaction:
 * scrolling the dialog content chains into the document (scrollY -> 73) and
 * overscroll rubber-bands it (scrollY -> -62). Stop both at their source:
 *  - overscroll-behavior: contain — the overlay's own scroll cannot chain out
 *    to the document (kills the +73 bleed and the -62 rubber-band).
 *  - touch-action: none on the scrim — dragging the backdrop cannot scroll the
 *    page behind it (a tap still dismisses; only pan/scroll is suppressed).
 * useDialogScrollFix additionally resets any pre-existing scroll on open. */
.v-overlay__content,
.v-overlay__content .v-card-text {
  overscroll-behavior: contain;
}

.v-overlay__scrim {
  touch-action: none;
}

.fb-banner-stack {
  position: fixed;
  top: calc(var(--v-layout-top, 64px) + var(--safe-area-inset-top));
  inset-inline: 16px;
  z-index: 1004;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
}

.fb-banner-stack > * {
  pointer-events: auto;
}

/* Offset top-aligned snackbars so they clear the app bar / safe area, same as .v-dialog above */
.v-snackbar--top {
  margin-top: calc(var(--safe-area-inset-top) + var(--v-layout-top, 64px)) !important;
}

/* Add safe area to bottom navigation; #839 shifts it to the physical screen bottom. */
html.pwa-installed .v-bottom-navigation {
  bottom: calc(-1 * var(--viewport-bottom-gap)) !important;
  padding-bottom: var(--safe-area-inset-bottom);
  height: calc(56px + var(--safe-area-inset-bottom)) !important;
  background-color: rgb(var(--v-theme-surface)) !important;
}

/* Belt-and-suspenders: paint the bottom safe-area strip with the nav's
 * surface color from the first static frame, driven by native env() so it
 * never depends on the JS-written --safe-area-inset-* value (which can be a
 * stale 0px during iOS cold start). max() uses whichever source is larger.
 * #839 shifts it to the physical screen bottom. */
html.pwa-installed .v-application::after {
  content: '';
  position: fixed;
  right: 0;
  bottom: calc(-1 * var(--viewport-bottom-gap));
  left: 0;
  height: max(var(--safe-area-inset-bottom), env(safe-area-inset-bottom, 0px));
  background-color: rgb(var(--v-theme-surface));
  /* Keep below .v-app-bar's z-index of 1005 so the app bar always stacks above the bottom safe-area strip */
  z-index: 1003;
  pointer-events: none;
}

/* v-main bottom clearance for the bottom nav (only rendered when installed) */
html.pwa-installed .v-main {
  padding-bottom: calc(56px + var(--safe-area-inset-bottom)) !important;
}

/* Apply bottom-nav safe area and clearance in browser mode too. */
html.fb-has-bottom-nav .v-bottom-navigation {
  bottom: calc(-1 * var(--viewport-bottom-gap)) !important;
  padding-bottom: var(--safe-area-inset-bottom);
  height: calc(56px + var(--safe-area-inset-bottom)) !important;
  background-color: rgb(var(--v-theme-surface)) !important;
}

html.fb-has-bottom-nav .v-main {
  padding-bottom: calc(56px + var(--safe-area-inset-bottom)) !important;
}

html.pwa-installed:not(.fb-has-bottom-nav) .v-main {
  padding-bottom: var(--safe-area-inset-bottom) !important;
}

/* Constrain centered dialogs to also clear the bottom nav when installed.
 * Sheets are exempt: they anchor to the bottom and their overlay covers the nav.
 * Overrides the top-only rule above (same !important specificity;
 * this rule is declared later in the stylesheet, so it wins the cascade
 * when the installed class is present). */
html.pwa-installed .v-dialog:not(.fb-bottom-sheet) {
  bottom: calc(56px + var(--safe-area-inset-bottom)) !important;
}

html.pwa-installed .v-dialog:not(.fb-bottom-sheet) .v-overlay__content {
  max-height: calc(100dvh - var(--v-layout-top, 64px) - var(--safe-area-inset-top) - 56px - var(--safe-area-inset-bottom)) !important;
}

/* Additional padding for bottom navigation in standalone mode */
@media (display-mode: standalone) {
  /* Belt-and-suspenders: paint the bottom safe-area strip with the nav's
   * surface color from the first static frame, driven by native env() so it
   * never depends on the JS-written --safe-area-inset-* value (which can be a
   * stale 0px during iOS cold start). max() uses whichever source is larger.
   * #839 shifts it to the physical screen bottom. */
  .v-application::after {
    content: '';
    position: fixed;
    right: 0;
    bottom: calc(-1 * var(--viewport-bottom-gap));
    left: 0;
    height: max(var(--safe-area-inset-bottom), env(safe-area-inset-bottom, 0px));
    background-color: rgb(var(--v-theme-surface));
    /* Keep below .v-app-bar's z-index of 1005 so the app bar always stacks above the bottom safe-area strip */
    z-index: 1003;
    pointer-events: none;
  }

  /* Optimize for standalone display mode - app-like experience */
  body {
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;
    overscroll-behavior-y: contain;
  }

  /* Keep inputs selectable for text entry */
  input,
  textarea,
  [contenteditable] {
    -webkit-user-select: text;
    user-select: text;
  }

  /* Prevent zoom on input focus for iOS (requires 16px minimum font-size) */
  input[type='text'],
  input[type='email'],
  input[type='password'],
  input[type='tel'],
  input[type='number'],
  textarea {
    font-size: 16px;
  }
}
</style>
